import React, { useCallback, useEffect, useState } from "react";
import { getLoopDetails, submitEditedLoop } from "../../services/loop";
import {
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Slider,
} from "@mui/material";
import { Film, Repeat } from "react-feather";
import { DownloadVideo, GetAllVideos } from "../../services/video";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import AlertModal from "../../components/modal";
import PopSnackbar from "../../components/snackbar";

const styles = {
  container: {
    padding: "20px",
    backgroundColor: "#f9f9f9",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  title: {
    marginBottom: "20px",
    fontSize: "1.5rem",
    fontFamily: "SeagoeUIBold",
  },
  videoList: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "10px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  listItem: {
    padding: "10px",
    borderBottom: "1px solid #eee",
  },
  videoPreview: {
    marginTop: "20px",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
    "& .MuiSelect-outlined": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
  },
};

export default function EditLoop() {
  const [data, setData] = useState(null); // Store loop data
  const [loading, setLoading] = useState(false);

  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");

  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(null);
  const [swapAll, setSwapAll] = useState(false);
  const [selectedVideoDetails, setSelectedVideoDetails] = useState([]); // Store details for selected videos
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [newTime, setNewTime] = useState(0);
  const [swapCount, setSwapCount] = useState(1); // Track the number of replacements
  const [playlistLength, setPlaylistLength] = useState(0);
  const [categoryStats, setCategoryStats] = useState([]);

  // Get the loop ID from the URL
  const loopId = window.location.href.split("/").pop();
  const navigator = useNavigate();

  const getSequentialIndices = (allIndices, startFromIndex, count) => {
    // Start from the clicked video index and take subsequent occurrences sequentially
    if (count >= allIndices.length) {
      return allIndices; // Return all if count exceeds available
    }

    // Find the position of the clicked video in the list of all occurrences
    const startPosition = allIndices.indexOf(startFromIndex);

    if (startPosition === -1) {
      // Fallback: if clicked index not found, start from beginning
      return allIndices.slice(0, count);
    }

    const result = [];

    // Take 'count' indices starting from the clicked position
    for (let i = 0; i < count; i++) {
      const position = (startPosition + i) % allIndices.length;
      result.push(allIndices[position]);
    }

    return result;
  };

  const formatDuration = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}' ${sec}''`;
  };

  const fetchLoopVideos = async () => {
    try {
      setLoading(true);

      const response = await getLoopDetails(loopId);

      const filteredVideos = response.data.videos?.filter(
        (video) => video.video_id !== 999999 && video.category_id != null
      );

      const durationByCategory = {
        "Campanii publicitare": { duration: 0, count: 0 },
        "Spoturi de locatie": { duration: 0, count: 0 },
        "Continut informativ": { duration: 0, count: 0 },
      };

      // Map category_id to label
      const getCategoryLabel = (categoryId) => {
        if (categoryId === 1) return "Campanii publicitare";
        if (categoryId === 2) return "Spoturi de locatie";
        if (categoryId === 3 || categoryId === 0) return "Continut informativ"; // treat 0 as 3
        return null;
      };

      filteredVideos.forEach((videoItem) => {
        const category = getCategoryLabel(videoItem.category_id);
        const duration =
          videoItem.video.exact_duration || videoItem.video.duration || 0;

        if (category) {
          durationByCategory[category].duration += duration;
          durationByCategory[category].count += 1;
        }
      });

      const totalDuration = Object.values(durationByCategory).reduce(
        (acc, val) => acc + val.duration,
        0
      );

      const percentages = Object.entries(durationByCategory).map(
        ([category, { duration, count }]) => ({
          category,
          duration,
          count,
          percent: totalDuration
            ? ((duration / totalDuration) * 100).toFixed(2)
            : "0.00",
        })
      );

      setCategoryStats(percentages);
      setData({ ...response, videos: filteredVideos });
      calculatePlaylistLength(filteredVideos);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoading(false);
    }
  };

  const calculatePlaylistLength = (videos) => {
    const totalDuration = videos.reduce(
      (acc, video) => acc + video.video.exact_duration || video.exact_duration,
      0
    );

    //trim it to 2 decimal places
    setPlaylistLength(totalDuration.toFixed(2));
  };

  const handleSelectChange = async (e) => {
    const videoIds = e.target.value; // Array of selected IDs
    setSelectedVideoId(videoIds);

    try {
      setIsLoadingPreview(true);

      const newVideoDetails = [];

      for (const videoId of videoIds) {
        const existingVideo = selectedVideoDetails.find(
          (video) => video.id === videoId
        );
        if (existingVideo) {
          newVideoDetails.push(existingVideo); // Keep existing details
          continue;
        }

        const video = videos.find((video) => video.ID === videoId);

        // Fetch SAS URL directly instead of creating an object URL
        const { url } = await DownloadVideo(videoId);

        newVideoDetails.push({
          id: videoId,
          title: video.title,
          url, // Use SAS URL directly
          duration: video.duration || 0,
          exact_duration: video.exact_duration || 0,
          categories: video.categories || [], // Include categories
          isLandscape: video.is_landscape,
        });
      }

      setSelectedVideoDetails(newVideoDetails);
    } catch (error) {
      console.error("Error downloading videos:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleStatus = (description, setStatus, setDescription) => {
    setStatus(true);
    setDescription(description);
    setTimeout(() => {
      setStatus(false);
      setDescription("");
    }, 5000);
  };

  const fetchVideosByCategory = async (videoId) => {
    try {
      const content = await GetAllVideos();
      console.log("Fetched videos:", content.data);

      // Get the current video's categories to filter by
      const currentVideo = data.videos.find((v) => v.video?.ID === videoId);
      const currentVideoCategories = currentVideo?.video?.categories || [];
      const currentVideoParentIds = new Set(
        currentVideoCategories.map((category) =>
          mapParentId(category.parent_id)
        )
      );

      // Get the device's partner_id for location-based filtering
      const devicePartnerId = data?.data?.devices?.[0]?.location?.partner_id;

      const filteredVideos = content.data
        .filter(
          (video) =>
            video.ID !== videoId &&
            video.ID !== 999999 &&
            Array.isArray(video.categories) &&
            video.categories.length > 0
        )
        .filter((video) => {
          // Filter videos that have at least one matching parent category
          const videoParentIds = new Set(
            video.categories.map((category) => mapParentId(category.parent_id))
          );
          const hasMatchingCategory = [...currentVideoParentIds].some((parentId) =>
            videoParentIds.has(parentId)
          );

          // For category 2 videos (Spoturi de locatie), also check partner_id match
          if (hasMatchingCategory && videoParentIds.has(2) && devicePartnerId) {
            // Check if the video has locations with matching partner_id
            const hasMatchingPartner = video.locations?.some(
              (location) => location.partner_id === devicePartnerId
            );
            return hasMatchingPartner;
          }

          return hasMatchingCategory;
        })
        .map((video) => {
          // Collect unique parent category names using a Set
          let parentCategoryNames = [
            ...new Set(
              video.categories.map((category) => {
                const parentId = mapParentId(category.parent_id);
                switch (parentId) {
                  case 1:
                    return "Campanii publicitare";
                  case 2:
                    return "Spoturi de locatie";
                  case 3:
                    return "Continut informativ";
                  default:
                    return "Categorie necunoscută"; // Handle unknown cases
                }
              })
            ),
          ];

          // If "Spoturi de locatie" is present, replace it with partner names
          if (parentCategoryNames.includes("Spoturi de locatie")) {
            const partnerNames = [
              ...new Set(
                video.locations.map(
                  (location) => location.partner?.name || "Unknown Partner"
                )
              ),
            ].join(", "); // Collect unique partner names and join them
            const partnerString = partnerNames
              ? `Parteneri: ${partnerNames}`
              : "Parteneri: Unknown"; // Fallback if no partner names are found

            // Replace "Spoturi de locatie" with the partner string
            parentCategoryNames = parentCategoryNames.map((category) =>
              category === "Spoturi de locatie" ? partnerString : category
            );
          }

          // Combine category/partner names into a single string
          const parentCategoryString = parentCategoryNames.join(", ");

          // Append parent categories/partners to the video title
          return {
            ...video,
            title: `${video.title} -> ${parentCategoryString}`, // Modify the title
          };
        });

      setVideos(filteredVideos);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const submitVideoOrderHandler = useCallback(
    async (forceProceed) => {
      if (!data || !data.videos || !loopId) {
        console.error("Missing data or loopId.");
        return;
      }

      const videoOrder = data.videos
        .filter((videoItem) => videoItem.video?.ID)
        .map((videoItem) => videoItem.video.ID);

      try {
        setLoading(true);
        const response = await submitEditedLoop(
          videoOrder,
          loopId,
          forceProceed
        );
        if (response.status === "success") {
          navigator(-1);
        } else {
          setConfirmationDialogOpen(true);
          setNewTime(response.duration);
        }
      } catch (error) {
        handleStatus(error.message, setToastStatus, setToastDescription);
      } finally {
        setLoading(false);
      }
    },
    [data, loopId]
  );

  useEffect(() => {
    fetchLoopVideos();
  }, []);

  const handleSwapClick = (index, videoId) => {
    fetchVideosByCategory(videoId);
    setCurrentVideoIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVideoId(null);
    setVideos([]);
    setSwapAll(false);
    setSelectedVideoDetails([]);
    setSwapCount(1);
    setIsLoadingPreview(false); // If you have this state affecting UI
  };

  const handleSubmitSwap = () => {
    if (selectedVideoId && selectedVideoId.length > 0) {
      const selectedVideoIdValue = selectedVideoId[0];
      const selectedVideo = selectedVideoDetails.find(
        (v) => v.id === selectedVideoIdValue
      );

      if (!selectedVideo) {
        console.error(
          "Selected video not found in the selectedVideoDetails list."
        );
        return;
      }

      // Make a shallow copy first, but DO NOT update it yet
      const updatedVideos = [...data.videos];
      const currentVideoId = updatedVideos[currentVideoIndex]?.video?.ID;

      let processedVideos;

      if (swapAll) {
        processedVideos = updatedVideos.map((videoItem) =>
          videoItem.video.ID === currentVideoId
            ? {
                ...videoItem,
                video: {
                  ...selectedVideo,
                  ID: selectedVideo.id,
                  categories: selectedVideo.categories || [],
                },
              }
            : videoItem
        );
      } else {
        const currentVideoIndices = updatedVideos
          .map((videoItem, index) =>
            videoItem.video.ID === currentVideoId ? index : null
          )
          .filter((index) => index !== null);

        const sequentialIndices = getSequentialIndices(
          currentVideoIndices,
          currentVideoIndex,
          swapCount
        );
        processedVideos = updatedVideos.map((videoItem, index) =>
          sequentialIndices.includes(index)
            ? {
                ...videoItem,
                video: {
                  ...selectedVideo,
                  ID: selectedVideo.id,
                  categories: selectedVideo.categories || [],
                },
              }
            : videoItem
        );
      }

      // Check the total duration BEFORE setting state
      const newTotalDuration = processedVideos.reduce(
        (acc, video) =>
          acc + (video.video.exact_duration || video.video.duration || 0),
        0
      );

      if (newTotalDuration > 12000) {
        alert(
          `The total playlist length after swapping would be ${Math.floor(
            newTotalDuration / 60
          )}' ${Math.floor(
            newTotalDuration % 60
          )}''. This exceeds the limit of 200 minutes. Please select a shorter video.`
        );
        return; // Stop execution if limit exceeded
      }

      // Now it's safe to set state because we passed the duration check
      setData((prevData) => ({
        ...prevData,
        videos: processedVideos,
      }));

      calculatePlaylistLength(processedVideos);
      handleClose(); // Only close when everything is okay
    } else {
      console.error("No video selected or invalid selectedVideoId.");
    }
  };

  const mapParentId = (parentId) => {
    if ([1, 4, 5, 8, 9, 10, 11, 12, 13, 14, 15, 16].includes(parentId)) {
      return 1;
    } else if (parentId === null || parentId === "") {
      return 2;
    } else if ([3, 6, 7, 17].includes(parentId)) {
      return 3;
    } else {
      return parentId; // Default case if needed
    }
  };

  const onDragEnd = (result) => {
    // If dropped outside the list, do nothing
    if (!result.destination) return;

    // Reorder the videos array
    const reorderedVideos = Array.from(data.videos);
    const [removed] = reorderedVideos.splice(result.source.index, 1);
    reorderedVideos.splice(result.destination.index, 0, removed);

    // Update the state with the new order
    setData((prevData) => ({
      ...prevData,
      videos: reorderedVideos,
    }));

    // Explicitly blur the focused element to prevent it from staying selected
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div style={styles.container}>
      {!loading && data && (
        <div
          style={{
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            {data.data.devices[0].name && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                    marginBottom: "4px",
                    fontFamily: "SeagoeUI",
                  }}
                >
                  Device
                </span>
                <span
                  style={{
                    fontSize: "1rem",
                    color: "#333",
                    fontFamily: "SeagoeUIBold",
                  }}
                >
                  {data.data.devices[0].name}
                </span>
                {data.data.devices[0].device_id && (
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: "#888",
                      fontFamily: "SeagoeUI",
                    }}
                  >
                    {data.data.devices[0].device_id}
                  </span>
                )}
              </div>
            )}

            {/* Location Information */}
            {data.data.devices[0].location.name && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                    marginBottom: "4px",
                    fontFamily: "SeagoeUI",
                  }}
                >
                  Locatie
                </span>
                <span
                  style={{
                    fontSize: "1rem",
                    color: "#333",
                    fontFamily: "SeagoeUIBold",
                  }}
                >
                  {data.data.devices[0].location.name}
                </span>
                {(data.data.devices[0].location.county ||
                  data.data.devices[0].location.city) && (
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: "#888",
                      fontFamily: "SeagoeUI",
                    }}
                  >
                    {data.data.devices[0].location.city &&
                    data.data.devices[0].location.county
                      ? `${data.data.devices[0].location.city}, ${data.data.devices[0].location.county}`
                      : data.data.devices[0].location.city ||
                        data.data.devices[0].location.county}
                  </span>
                )}
              </div>
            )}

            {/* Partner Information */}
            {data.data.devices[0].location.partner.name && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                    marginBottom: "4px",
                    fontFamily: "SeagoeUI",
                  }}
                >
                  Partener
                </span>
                <span
                  style={{
                    fontSize: "1rem",
                    color: "#333",
                    fontFamily: "SeagoeUIBold",
                  }}
                >
                  {data.data.devices[0].location.partner.name}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={{
          borderRadius: "10px",
          padding: "16px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "14px",
          }}
        >
          <span
            style={{
              fontSize: "1rem",
              color: "#666",
              fontFamily: "SeagoeUIBold",
            }}
          >
            Lungime spoturi
          </span>
          {loading ? (
            <CircularProgress size={22} thickness={2} sx={{ color: "#555" }} />
          ) : (
            <span
              style={{
                fontSize: "1.3rem",
                color: "#666",
                fontFamily: "SeagoeUIBold",
              }}
            >
              {Math.floor(playlistLength / 60)}'{" "}
              {Math.floor(playlistLength % 60)}''
            </span>
          )}
        </div>

        {!loading && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "10px",
            }}
          >
            {categoryStats.map((stat, index) => {
              const colors = {
                "Campanii publicitare": { color: "#FF5A5F", light: "#FFEEEE" },
                "Spoturi de locatie": { color: "#00A699", light: "#E6F7F6" },
                "Continut informativ": { color: "#FC642D", light: "#FFF1EC" },
              };

              const theme = colors[stat.category];

              return (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    minWidth: "150px",
                    background: theme.light,
                    borderRadius: "6px",
                    padding: "12px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "3px",
                      width: `${stat.percent}%`,
                      background: theme.color,
                      transition: "width 0.5s ease",
                    }}
                  />

                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: theme.color,
                      marginBottom: "8px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {stat.category}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "1.1rem",
                        fontFamily: "SeagoeUIBold",
                        color: "#333",
                      }}
                    >
                      {formatDuration(stat.duration)}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.8rem",
                        color: "#666",
                      }}
                    >
                      <span>{stat.count}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                          stroke="#666"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M9.5 8.96552V15.0345C9.5 15.5767 10.0507 15.9352 10.5287 15.6677L15.8537 12.6332C16.3175 12.3728 16.3175 11.6272 15.8537 11.3668L10.5287 8.33234C10.0507 8.06476 9.5 8.42328 9.5 8.96552Z"
                          fill="#666"
                        />
                      </svg>
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#888",
                      marginTop: "6px",
                    }}
                  >
                    {stat.percent}% din total
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        className="add-video-button"
        style={{ width: "10vw", marginBottom: "0.5rem", marginTop: "0.5rem" }}
        onClick={() => submitVideoOrderHandler(false)}
        disabled={loading}
      >
        Salveaza noua ordine
      </button>
      <Divider style={{ marginBottom: "20px" }} />
      {loading ? (
        <CircularProgress size={24} />
      ) : data ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="videoList">
            {(provided) => (
              <List
                dense
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={styles.videoList}
              >
                {data.videos?.map((videoItem, index) => {
                  if (!videoItem.video?.ID) {
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginLeft: "16px",
                          columnGap: "30px",
                        }}
                      >
                        <Film color="rgba(0, 0, 0, 0.54)" fontSize="small" />
                        <p style={{ fontStyle: "italic", fontSize: "14px" }}>
                          This video was deleted
                        </p>
                      </div>
                    );
                  }

                  const uniqueParentIds = new Set(
                    videoItem.video?.categories?.map((category) =>
                      mapParentId(category.parent_id)
                    )
                  );

                  // Generate a unique ID for draggable items
                  const uniqueDraggableId = `${videoItem.video.ID}-${index}`;

                  return (
                    <Draggable
                      key={uniqueDraggableId}
                      draggableId={uniqueDraggableId}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={
                            snapshot.isDragging
                              ? { background: "rgb(235,235,235)" }
                              : ""
                          }
                        >
                          <ListItemIcon>
                            <Film fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={videoItem.video.title.replace(/->.*/g, "")}
                            secondary={
                              <div>
                                <span>
                                  Durata: {videoItem.video?.duration} secunde
                                </span>
                                <span style={{ marginLeft: "10px" }}>
                                  Categorii:{" "}
                                  {Array.from(uniqueParentIds).map(
                                    (parentId, index) => (
                                      <span key={index}>
                                        {(() => {
                                          let categoryName;
                                          switch (parentId) {
                                            case 1:
                                              categoryName =
                                                "Campanii publicitare";
                                              break;
                                            case 2:
                                              categoryName =
                                                "Spoturi de locatie";
                                              break;
                                            case 3:
                                              categoryName =
                                                "Continut informativ";
                                              break;
                                            default:
                                              categoryName = "";
                                          }
                                          return categoryName;
                                        })()}{" "}
                                        {index < uniqueParentIds.size - 1
                                          ? ", "
                                          : ""}
                                      </span>
                                    )
                                  )}
                                </span>
                              </div>
                            }
                          />
                          <Tooltip title="Inlocuieste Spot">
                            <Repeat
                              fontSize="small"
                              style={{ marginRight: "10px", cursor: "pointer" }}
                              onClick={() =>
                                handleSwapClick(index, videoItem.video.ID)
                              }
                            />
                          </Tooltip>
                        </ListItem>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Typography>Nu exista date</Typography>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Autocomplete
            options={videos}
            getOptionLabel={(video) => video.title}
            onChange={(event, newValue) => {
              setSelectedVideoId(newValue?.ID || null);
              handleSelectChange({
                target: { value: newValue ? [newValue.ID] : [] },
              });
            }}
            renderInput={(params) => (
              <TextField
                sx={styles.select}
                {...params}
                label="Selecteaza spot nou"
                placeholder="Cauta"
                fullWidth
              />
            )}
          />

          {selectedVideoDetails.map((video, index) => (
            <div key={video.id} style={{ marginBottom: "16px" }}>
              <h4 style={{ fontFamily: "SeagoeUIBold" }}>
                {video.title.replace(/->.*/g, "")}
              </h4>
              <video
                key={`select-${video.url}`}
                width="100%"
                controls
                style={{
                  marginBottom: 8,
                  borderRadius: 4,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
              >
                <source src={video.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p>Durata: {video.duration} secunde</p>
            </div>
          ))}
          <Typography variant="subtitle1" style={{ marginTop: "16px" }}>
            Selecteaza numarul de inlocuiri:
          </Typography>
          <Typography
            variant="body2"
            style={{ marginBottom: "8px", color: "#666", fontStyle: "italic" }}
          >
            Spot de inlocuit: "
            {data?.videos[currentVideoIndex]?.video?.title?.replace(
              /->.*/g,
              ""
            ) || "Necunoscut"}
            "
          </Typography>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Slider
              color="black"
              value={swapCount}
              onChange={(e, newValue) => {
                setSwapCount(newValue);
                setSwapAll(
                  newValue ===
                    data?.videos.filter(
                      (videoItem) =>
                        videoItem.video.ID ===
                        data?.videos[currentVideoIndex]?.video?.ID
                    ).length
                );
              }}
              valueLabelDisplay="auto"
              step={1}
              min={1}
              max={
                data?.videos.filter(
                  (videoItem) =>
                    videoItem.video.ID ===
                    data?.videos[currentVideoIndex]?.video?.ID
                ).length
              }
              style={{ flexGrow: 1, marginRight: "16px" }}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ whiteSpace: "nowrap" }}
            >
              Selectat: {swapCount} /{" "}
              {
                data?.videos.filter(
                  (videoItem) =>
                    videoItem.video.ID ===
                    data?.videos[currentVideoIndex]?.video?.ID
                ).length
              }
            </Typography>
          </div>

          <FormControlLabel
            control={
              <Checkbox
                color="black"
                checked={swapAll}
                onChange={(e) => {
                  setSwapAll(e.target.checked);
                  setSwapCount(
                    e.target.checked
                      ? data?.videos.filter(
                          (videoItem) =>
                            videoItem.video.ID ===
                            data?.videos[currentVideoIndex]?.video?.ID
                        ).length
                      : 1
                  );
                }}
              />
            }
            label="Inlocuieste toate spoturile"
          />
        </DialogContent>
        <DialogActions>
          <button className="add-video-button" onClick={handleClose}>
            Renunta
          </button>
          <button
            className="add-video-button"
            onClick={handleSubmitSwap}
            disabled={!selectedVideoId || isLoadingPreview}
          >
            Inlocuieste
          </button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={confirmationDialogOpen}
        handleClose={() => setConfirmationDialogOpen(false)}
        handleYes={async () => {
          setConfirmationDialogOpen(false);
          await submitVideoOrderHandler(true);
        }}
        text={
          <>
            Noua durata a playlist-ului intrece limita maxima. Durata noua a
            playlist-ului va fi{" "}
            <span style={{ fontFamily: "SeagoeUIBold" }}>
              {Math.floor(newTime / 60)}' {Math.floor(newTime % 60)}''
            </span>
            . Vrei sa continui?
          </>
        }
      />

      {toastStatus && <PopSnackbar description={toastDescription} />}
    </div>
  );
}
